import { useEffect, useMemo, useState } from 'react'
import type { UsuarioSesion } from '../../api/tipos.js'
import { rutasVisibles } from '../../navegacion/permisos.js'
import type { Vista } from '../../navegacion/rutas.js'
import { DisposicionNavegacion } from './DisposicionNavegacion.js'
import { EnlaceNavegacion } from './EnlaceNavegacion.js'
import { GrupoDesplegable } from './GrupoDesplegable.js'
import { SeccionNavegacion } from './SeccionNavegacion.js'
import { construirModeloNavegacion, type GrupoNavegacionModelo } from './modeloNavegacion.js'

export function NavegacionLateral({
  usuario,
  vistaActiva,
  onNavegar,
}: {
  usuario: UsuarioSesion
  vistaActiva: Vista
  onNavegar: () => void
}) {
  const modelo = useMemo(
    () => construirModeloNavegacion(rutasVisibles(usuario), vistaActiva),
    [usuario, vistaActiva],
  )
  const administracion = modelo.auxiliares.find((grupo) => grupo.id === 'administracion')
  const [administracionAbierta, setAdministracionAbierta] = useState(administracion?.activo ?? false)

  // Una ruta administrativa nunca queda oculta. Al volver al trabajo operativo
  // se recupera la divulgación progresiva y el grupo vuelve a quedar plegado.
  useEffect(() => {
    setAdministracionAbierta(administracion?.activo ?? false)
  }, [administracion?.activo])

  if (!modelo.principal) return null

  const enlaces = (grupo: GrupoNavegacionModelo, compacto: boolean) => grupo.destinos.map((destino) => (
    <EnlaceNavegacion
      key={destino.vista}
      destino={destino}
      compacto={compacto}
      onNavegar={onNavegar}
    />
  ))

  const seccion = (
    grupo: GrupoNavegacionModelo,
    prominencia: 'principal' | 'secundaria',
  ) => {
    const secundaria = prominencia === 'secundaria'
    if (secundaria && grupo.id === 'administracion' && grupo.destinos.length > 1) {
      return (
        <GrupoDesplegable
          key={grupo.id}
          id="navegacion-administracion"
          etiqueta={grupo.etiqueta}
          abierto={administracionAbierta}
          activo={grupo.activo}
          onAlternar={() => setAdministracionAbierta((abierta) => !abierta)}
        >
          {enlaces(grupo, true)}
        </GrupoDesplegable>
      )
    }

    return (
      <SeccionNavegacion
        key={grupo.id}
        id={`navegacion-${grupo.id}`}
        etiqueta={grupo.etiqueta}
        prominencia={prominencia}
        activa={grupo.activo}
        mostrarEtiqueta={grupo.destinos.length > 1}
      >
        {enlaces(grupo, secundaria)}
      </SeccionNavegacion>
    )
  }

  const principal = seccion(modelo.principal, 'principal')
  const auxiliar = modelo.auxiliares.length > 0
    ? modelo.auxiliares.map((grupo) => seccion(grupo, 'secundaria'))
    : undefined

  return <DisposicionNavegacion principal={principal} auxiliar={auxiliar} />
}
